// These live outside app.ts on purpose. app.ts imports `@embroider/virtual/compat-modules`, which
// statically imports every app module — so anything app modules import back from app.ts closes a
// cycle. Cold boot tolerates it (index.html enters at app.ts, so compat-modules finishes first),
// but after an HMR invalidation Vite re-enters the graph from a stamped leaf, app.ts's body runs
// while compat-modules is mid-evaluation, and its top-level `loadInitializers(..., compatModules)`
// dies on `Cannot access 'compatModules' before initialization`. Ember then never boots and the
// page renders blank with no error. Keep this module free of anything that reaches compat-modules.

import { AnalyticsBrowser } from "@segment/analytics-next"
import * as Sentry from "@sentry/browser"
import { objectId } from "@shared/util/object-id"
import config from "./config/environment"

export const analytics = new AnalyticsBrowser()
// @ts-ignore
window.analytics = analytics

// The first error ends the session: the modal below, then the reload. Later errors come from a
// page nobody is using any more, so reporting them only buries the one that mattered.
let hasHandledError = false

export function handleError(error: Error) {
  if (hasHandledError === true) return
  hasHandledError = true

  let id = objectId()

  console.error(`Error Id: ${id}`, error.name, error.message, error.stack, error)

  if (config.environment === "test") return

  notifySentry(error, id)
  postError(error, id)
  displayError(id)
  if (window && window.location && config.environment !== "development") {
    setTimeout(() => {
      window.location.reload()
    }, 30000)
  }

  // We send this without using the ajax service to be sure we're not getting/
  // caught up with app internals that might be broke.
}

export function notifySentry(error: unknown, id: string, fatal = true) {
  let userId = null
  let sessionId = null
  if (window.localStorage !== undefined) {
    let sessionStore = JSON.parse(localStorage.getItem("sessionStore") || "{}")
    userId = sessionStore["userId"]
    sessionId = sessionStore["sessionId"]
  }
  Sentry.withScope((scope) => {
    scope.setLevel(fatal ? Sentry.Severity.Fatal : Sentry.Severity.Error)
    scope.setExtra("errorId", id)
    scope.setTag("clientVersion", config.CLIENT_VERSION)
    scope.setTag("userId", userId)
    scope.setTag("sessionId", sessionId)
    scope.setTag("errorId", id)
    Sentry.captureException(error)
  })
  if (typeof window.gtag !== "undefined" && error instanceof Error) {
    window.gtag("event", "exception", {
      description: error.name,
      fatal,
    })
  }
}

export function postError(error: Error, id: string) {
  let host = window.location?.host || "www.commonplanner.com"

  let token = null
  let userId = null
  let sessionId = null
  if (window.localStorage !== undefined) {
    let sessionStore = JSON.parse(localStorage.getItem("sessionStore") || "{}")
    token = sessionStore["token"]
    userId = sessionStore["userId"]
    sessionId = sessionStore["sessionId"]
  }
  analytics.track("Client Error", {
    errorId: id,
    title: error.name,
    description: error.message,
    sessionLink: `https://${host}/admin/sessions/${sessionId}`,
    context: {
      sessionId: sessionId,
      userId: userId,
    },
  })

  let headers = { "Content-Type": "application/json; charset=utf-8" }
  if (token) headers["Authorization"] = `Bearer ${token}`
  if (sessionId) headers["X-SESSION-ID"] = sessionId
  fetch("/api/v4/actions", {
    method: "POST",
    headers: headers,
    body: JSON.stringify({
      action: {
        id: id,
        name: "CLIENT_ERROR",
        narrativeTitle: error.name,
        narrativeDescription: error.message,
        narrativeContext: {
          stackTrace: error.stack,
          sessionId: sessionId,
          userId: userId,
          url: window.location.href,
          sessionLink: `https://${host}/admin/sessions/${sessionId}`,
        },
      },
    }),
  })
}

function displayError(id) {
  if (window && window.swal) {
    window
      .swal({
        title: "There's been an error. We need to refresh your browser",
        text: `We've been notified. Error code: ${id}`,
        type: "warning",
        showCancelButton: false,
      })
      .then(() => {
        window.location.reload()
      })
      .catch(() => {
        window.location.reload()
      })
  }
}
